'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import type { ComponentProps } from 'react';

// Heavy code-split chunk. Google Maps + the full locations UI only mount
// after the placeholder enters (or approaches) the viewport. ssr:false keeps
// the maps client code out of the SSR bundle entirely.
const DispensaryLocationsInner = dynamic(() => import('./DispensaryLocations'), {
  ssr: false,
  loading: () => null,
});

type Props = ComponentProps<typeof DispensaryLocationsInner>;

/** Intersection-observer wrapper around DispensaryLocations.
 *  Lighthouse flagged ~168 KiB of unused Google Maps JS on initial load.
 *  The map is always below the fold, so we defer mounting until the section
 *  is within ~400 px of the viewport (room to load before the user reaches it). */
export default function DispensaryLocationsLazy(props: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (shouldRender) return;

    // No IntersectionObserver (very old browsers, jsdom) → render immediately.
    if (typeof IntersectionObserver === 'undefined') {
      setShouldRender(true);
      return;
    }

    const el = wrapperRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldRender]);

  return (
    <div ref={wrapperRef} className={shouldRender ? undefined : 'min-h-[600px]'}>
      {shouldRender ? <DispensaryLocationsInner {...props} /> : null}
    </div>
  );
}
